/**
 * Domain-allowlist primitives shared across the Worker ↔ Convex trust
 * boundary. Three consumers use this single source, so no two of them
 * can drift apart on what the allowlist accepts:
 * `apps/auth/worker/lib/domain-allowlist.ts` (the broker's re-export,
 * which `worker/lib/jwt.ts`'s `isDomainAllowed` is built on),
 * `packages/convex-identity/convex/lib/domainAllowlist.ts` (Convex), and
 * `packages/auth-consumer/src/return-to.ts`, which imports
 * `matchesAllowedDomain` here directly.
 *
 * MUST stay pure TypeScript: no `node:` imports, no framework imports,
 * no env access. Convex bundles this file into its deployment and every
 * consuming Worker bundles it into its script, so anything one of those
 * runtimes lacks breaks a bundle rather than a call. The broker is the
 * strictest of them — it sets no `nodejs_compat`, so it has no node
 * built-ins at all.
 */

/**
 * Validates a lowercase-normalized DNS name with at least two labels
 * (e.g. "example.com"). Rejects leading/trailing dots, consecutive dots
 * like "..com", and labels that start or end with a hyphen. Dot-less
 * names such as "localhost" do NOT match; callers special-case those.
 */
export const DOMAIN_NAME_REGEX =
  /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;

/**
 * Checks whether `domain` matches any entry in `allowedDomains`.
 * Wildcard entries `*.example.com` match `example.com` or any subdomain
 * (`a.example.com`, `a.b.example.com`); they do NOT match sibling
 * registrable domains such as `evilexample.com`.
 *
 * Callers should pass a lowercase-normalized `domain`; allow entries are
 * lowercased internally.
 */
export function matchesAllowedDomain(
  domain: string,
  allowedDomains: readonly string[],
): boolean {
  for (const raw of allowedDomains) {
    const allowed = raw.trim().toLowerCase();
    if (!allowed) continue;

    if (allowed.startsWith("*.")) {
      const baseDomain = allowed.substring(2);
      if (!baseDomain) continue;
      if (domain === baseDomain || domain.endsWith("." + baseDomain)) {
        return true;
      }
    } else if (domain === allowed) {
      return true;
    }
  }

  return false;
}
